import {
  OktonSearchSelect,
  OktonPageHeader,
  OktonInput,
  OktonAlert,
  OktonEmptyState,
  OktonCheckbox,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check, Wallet } from 'lucide-react';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import CaixaField from '../../../features/caixa/CaixaField';
import BankAppPage from '../../../layout/BankAppPage';
import {
  usaControlaDRE,
  useMoedasPagamento,
  useParametros,
  usePlanosDFC,
} from '../../../services/hooks/useCadastros';
import { useContasBancarias } from '../../../services/hooks/useContasBancarias';
import {
  useChequesDisponiveis,
  useCreateDeposito,
} from '../../../services/hooks/useDepositos';
import { formatCustomDate } from '../../../utils/formatCustomDate';
import formatValue from '../../../utils/formatValue';
import { limparEntradaValor, valorParaNumero } from '../../../utils/valorInput';
import { EstadoNavegacaoMonitor } from '../../monitor-bancario/estadoNavegacao';

// Tipo de documento da Moeda de Cheque de Terceiro, como o IFLEX numera.
const TIPO_TITULO_CHEQUETERCEIRO = '14';

const VAZIO = {
  conta: '',
  moeda: '',
  data: new Date().toISOString().split('T')[0],
  valor: '',
  envelope: '',
  planocontas: '',
  caixa: '',
  observacao: '',
};

const NovoDeposito = () => {
  const history = useHistory();
  const { state } = useLocation<EstadoNavegacaoMonitor | undefined>();
  const destino =
    state?.voltarPara === '/monitor-bancario'
      ? '/monitor-bancario'
      : '/depositos';
  const bg = useColorModeValue('white', 'gray.800');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  const { data: contas = [] } = useContasBancarias();
  const { data: planosDFC = [] } = usePlanosDFC();
  const { data: moedas = [] } = useMoedasPagamento();
  const { data: parametros } = useParametros();
  const controlaDRE = usaControlaDRE(parametros);
  const createMutation = useCreateDeposito();

  const [form, setForm] = useState({ ...VAZIO, conta: state?.conta ?? '' });
  const voltar = () => {
    if (destino === '/monitor-bancario') {
      history.push(destino, { conta: form.conta, periodo: state?.periodo });
    } else {
      history.push(destino);
    }
  };
  const [error, setError] = useState('');
  const [chequesEscolhidos, setChequesEscolhidos] = useState<string[]>([]);

  // Abaixo do estado de propósito: o bloco lê a Moeda escolhida no form, e
  // usá-la antes de declarar não compila.
  //
  // Só a Moeda de Cheque de Terceiro deposita papel: nela o valor não é
  // digitado, e sim a soma dos cheques escolhidos.
  const moedaEscolhida = moedas.find(m => m.id_moe === form.moeda);
  const ehCheque =
    moedaEscolhida?.tipodocumento_tpt === TIPO_TITULO_CHEQUETERCEIRO;
  const { data: chequesDisponiveis = [] } = useChequesDisponiveis(ehCheque);
  const somaCheques = chequesDisponiveis
    .filter(c => chequesEscolhidos.includes(c.id_fin))
    .reduce((total, c) => total + Number(c.valor_fin), 0);

  const alternarCheque = (id: string) => {
    setChequesEscolhidos(atual =>
      atual.includes(id) ? atual.filter(c => c !== id) : [...atual, id],
    );
  };

  const salvar = async () => {
    if (!form.conta) {
      setError('Informe a Conta Bancária.');
      return;
    }
    if (!form.moeda) {
      setError('Informe a forma de pagamento depositada.');
      return;
    }
    if (!form.data) {
      setError('Informe a data do Depósito.');
      return;
    }
    if (ehCheque && chequesEscolhidos.length === 0) {
      setError('Escolha ao menos um cheque para depositar.');
      return;
    }
    // Com cheques o valor é a soma deles, e quem soma de verdade é o
    // servidor: ele relê o valor de cada papel na base.
    const valorNumerico = ehCheque ? somaCheques : valorParaNumero(form.valor);
    if (!valorNumerico) {
      setError('Informe um valor maior que zero.');
      return;
    }
    if (controlaDRE && !form.planocontas) {
      setError('Informe o Plano de Contas (DFC).');
      return;
    }
    setError('');
    try {
      await createMutation.mutateAsync({
        conta: form.conta,
        moeda: form.moeda,
        cheques: ehCheque ? JSON.stringify(chequesEscolhidos) : '',
        data: form.data,
        valor: String(Math.abs(valorNumerico)),
        envelope: form.envelope.trim(),
        planocontas: form.planocontas,
        caixa: form.caixa,
        observacao: form.observacao.trim(),
      });
      voltar();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Erro ao gravar o Depósito.',
      );
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Novo Depósito"
        subtitle="Entrada do Caixa para a Conta Bancária"
        onBack={voltar}
      />

      <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
        {/* Com Cheque de Terceiro os papéis disponíveis ficam à direita: são
        uma lista, e embaixo do formulário empurrariam o botão de salvar para
        fora da tela. Sem cheque, a tela fica como sempre foi. */}
        <Stack spacing="4" maxW={ehCheque ? undefined : '720px'}>
          {error && <OktonAlert status="error" description={error} />}
          {/* A coluna da esquerda tem largura fixa: ligar a lista de cheques
          não pode mudar o tamanho dos campos. A direita fica com o que
          sobra. */}
          <Flex gridGap="8" align="flex-start" flexWrap="wrap">
            <Stack spacing="4" w="100%" maxW="720px">
              <Box>
                <Text
                  as="label"
                  htmlFor="conta"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Conta Bancária *
                </Text>
                <OktonSearchSelect
                  name="conta"
                  placeholder="Selecione a conta"
                  value={form.conta}
                  onChange={valor => setForm(f => ({ ...f, conta: valor }))}
                  options={[
                    ...contas.map(c => ({
                      value: c.id_cts,
                      label: c.descricao_cts,
                    })),
                  ]}
                />
              </Box>

              <SimpleGrid columns={2} spacing="4">
                <Box>
                  <Text
                    as="label"
                    htmlFor="moeda"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    Moeda Depositada *
                  </Text>
                  <OktonSearchSelect
                    name="moeda"
                    placeholder="Selecione"
                    value={form.moeda}
                    onChange={valor => setForm(f => ({ ...f, moeda: valor }))}
                    options={[
                      ...moedas.map(m => ({
                        value: m.id_moe,
                        label: m.nome_moe,
                      })),
                    ]}
                  />
                </Box>
                <Box>
                  <Text
                    as="label"
                    htmlFor="deposito-envelope"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    Envelope
                  </Text>
                  <OktonInput
                    name="deposito-envelope"
                    size="lg"
                    rounded="2xl"
                    fontWeight="light"
                    color={color}
                    maxLength={60}
                    value={form.envelope}
                    onChange={e => {
                      const valor = e.target.value;
                      setForm(f => ({ ...f, envelope: valor }));
                    }}
                  />
                </Box>
              </SimpleGrid>

              <SimpleGrid columns={2} spacing="4">
                <Box>
                  <Text
                    as="label"
                    htmlFor="deposito-data"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    Data *
                  </Text>
                  <OktonInput
                    name="deposito-data"
                    type="date"
                    size="lg"
                    rounded="2xl"
                    fontWeight="light"
                    color={color}
                    value={form.data}
                    onChange={e => {
                      const valor = e.target.value;
                      setForm(f => ({ ...f, data: valor }));
                    }}
                  />
                </Box>
                <Box>
                  <Text
                    as="label"
                    htmlFor="deposito-valor"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    Valor (R$) *
                  </Text>
                  <OktonInput
                    name="deposito-valor"
                    type="text"
                    inputMode="decimal"
                    size="lg"
                    rounded="2xl"
                    fontWeight="light"
                    placeholder="0,00"
                    color={color}
                    isReadOnly={ehCheque}
                    value={ehCheque ? formatValue(somaCheques) : form.valor}
                    onChange={e => {
                      const valor = e.target.value;
                      setForm(f => ({
                        ...f,
                        valor: limparEntradaValor(valor),
                      }));
                    }}
                  />
                  {ehCheque && (
                    <Text fontSize="xs" color="gray.500" mt="1">
                      Soma dos cheques escolhidos.
                    </Text>
                  )}
                </Box>
              </SimpleGrid>

              <Box>
                <Text
                  as="label"
                  htmlFor="planocontas"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Plano de Contas (DFC) {controlaDRE && '*'}
                </Text>
                <OktonSearchSelect
                  name="planocontas"
                  placeholder="Sem classificação"
                  value={form.planocontas}
                  onChange={valor =>
                    setForm(f => ({ ...f, planocontas: valor }))
                  }
                  options={[
                    ...planosDFC.map(p => ({
                      value: p.id_pct,
                      label: `${p.item_pct} ${p.nome_pct}`,
                    })),
                  ]}
                />
              </Box>

              <CaixaField
                data={form.data}
                valor={form.caixa}
                onChange={caixa => setForm(f => ({ ...f, caixa }))}
              />

              <Box>
                <Text
                  as="label"
                  htmlFor="deposito-observacao"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Observação
                </Text>
                <OktonInput
                  name="deposito-observacao"
                  size="lg"
                  rounded="2xl"
                  fontWeight="light"
                  color={color}
                  maxLength={1024}
                  value={form.observacao}
                  onChange={e => {
                    const valor = e.target.value;
                    setForm(f => ({ ...f, observacao: valor }));
                  }}
                />
              </Box>

              <Text fontSize="xs" color="gray.500">
                O Depósito sai do Caixa aberto, como no IFLEX, e nasce Pendente
                — a compensação é feita na tela de Depósito do IFLEX. Sem Caixa
                aberto o lançamento é recusado.
              </Text>
            </Stack>

            {ehCheque && (
              <Stack spacing="4" flex="1" minW="320px">
                <Flex align="center" gridGap="2">
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    textTransform="uppercase"
                  >
                    Cheques disponíveis ({chequesDisponiveis.length})
                  </Text>
                </Flex>

                {chequesDisponiveis.length === 0 ? (
                  <Box borderWidth="1px" borderRadius="lg">
                    <OktonEmptyState
                      icon={Wallet}
                      title="Nenhum cheque para depositar"
                      description="Aparecem aqui os cheques em mãos e os devolvidos pelo banco. Cheque já depositado não reaparece."
                    />
                  </Box>
                ) : (
                  <Stack
                    spacing="0"
                    borderWidth="1px"
                    borderRadius="lg"
                    overflow="hidden"
                    maxH="420px"
                    overflowY="auto"
                  >
                    {chequesDisponiveis.map(c => (
                      <Flex
                        key={c.id_fin}
                        align="center"
                        gridGap="3"
                        px="4"
                        py="3"
                        borderBottomWidth="1px"
                        bg={
                          chequesEscolhidos.includes(c.id_fin)
                            ? subtleBg
                            : undefined
                        }
                      >
                        <Box flexShrink={0}>
                          <OktonCheckbox
                            name={`deposito-cheque-${c.id_fin}`}
                            label={null}
                            size="md"
                            aria-label={`Selecionar cheque ${
                              c.documento_fin ?? 'sem número'
                            } de ${
                              c.nomecliente_fin || c.nome_cli || 'Sem emitente'
                            }`}
                            isChecked={chequesEscolhidos.includes(c.id_fin)}
                            onChange={() => alternarCheque(c.id_fin)}
                          />
                        </Box>
                        <Box flex="1" minW="0">
                          <Text
                            fontSize="sm"
                            color={color}
                            whiteSpace="normal"
                            wordBreak="normal"
                          >
                            {c.nomecliente_fin || c.nome_cli || 'Sem emitente'}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            Nº {c.documento_fin ?? '—'}
                            {c.nome_ban ? ` — ${c.nome_ban}` : ''}
                            {c.vencimento_fin
                              ? ` — ${formatCustomDate(
                                  c.vencimento_fin.slice(0, 10),
                                  'yyyy-MM-dd',
                                  'dd/MM/yyyy',
                                )}`
                              : ''}
                          </Text>
                        </Box>
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color={color}
                          whiteSpace="nowrap"
                          flexShrink={0}
                        >
                          {formatValue(Number(c.valor_fin))}
                        </Text>
                      </Flex>
                    ))}
                  </Stack>
                )}

                <Flex justify="space-between" align="center">
                  <Text fontSize="xs" color="gray.500">
                    {chequesEscolhidos.length} escolhido(s)
                  </Text>
                  <Text fontWeight="semibold" color={color}>
                    {formatValue(somaCheques)}
                  </Text>
                </Flex>
              </Stack>
            )}
          </Flex>

          <Flex gridGap="3" pt="2">
            <Button variant="ghost" onClick={voltar}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={
                createMutation.isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <Check size={16} />
                )
              }
              isDisabled={createMutation.isLoading}
              onClick={salvar}
            >
              Cadastrar
            </Button>
          </Flex>
        </Stack>
      </Box>
    </BankAppPage>
  );
};

export default NovoDeposito;
